import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, LifeBuoy, BookOpen, MessageSquare, ExternalLink, Loader2, AlertTriangle, Layers, PoundSterling, Search } from "lucide-react";
import SEO from "@/lib/seo-static.json";
import { SITE } from "@/lib/site";
import { Container, Kicker, CTAButton, usePageTitle } from "@/components/ui-kit";
import { CTABand } from "@/components/sections/Social";

export const HELP_EMBED_SRC = "https://fixlr.app/help-embed.js";
export const HELP_APP_URL = "https://fixlr.app/help";
const EMPTY_TIMEOUT_MS = 12000;
const POPULAR = ["RAMS", "approve a quote", "payroll", "clock in", "client portal", "Xero"];

const QUICK = [
  { icon: Layers, label: "Every module, explained", to: "/product" },
  { icon: BookOpen, label: "H&S library preview", to: "/library" },
  { icon: PoundSterling, label: "Pricing & plan builder", to: "/pricing" },
  { icon: MessageSquare, label: "Talk to the team", to: "/contact" },
];

// The embed binds to one DOM node for the page's lifetime, so we keep a single #fixlr-help root alive across
// route changes (parked in a hidden holder) and inject the fixlr.app script only once.
const embedRoot = () => {
  let root = document.getElementById("fixlr-help");
  if (!root) { root = document.createElement("div"); root.id = "fixlr-help"; }
  return root;
};
const parking = () => {
  let p = document.getElementById("fixlr-help-parking");
  if (!p) { p = document.createElement("div"); p.id = "fixlr-help-parking"; p.hidden = true; document.body.appendChild(p); }
  return p;
};

const useHelpEmbed = (hostRef) => {
  const [state, setState] = useState(() => (window.__fxhReady ? "ready" : "loading"));
  useEffect(() => {
    const host = hostRef.current;
    const root = embedRoot();
    host.appendChild(root);
    if (window.__fxhReady) { window.dispatchEvent(new HashChangeEvent("hashchange")); return () => parking().appendChild(root); }
    let done = false;
    const settle = (s) => { if (!done) { done = true; if (s === "ready") window.__fxhReady = true; setState(s); } };
    const observer = new MutationObserver(() => { if (root.childNodes.length) { settle("ready"); observer.disconnect(); } });
    observer.observe(root, { childList: true });
    if (!document.querySelector(`script[src="${HELP_EMBED_SRC}"]`)) {
      const script = document.createElement("script");
      script.src = HELP_EMBED_SRC; script.async = true;
      script.onload = () => settle("ready");
      script.onerror = () => settle("error");
      document.body.appendChild(script);
    }
    const timer = setTimeout(() => settle(root.childNodes.length ? "ready" : "error"), EMPTY_TIMEOUT_MS);
    return () => { observer.disconnect(); clearTimeout(timer); parking().appendChild(root); };
  }, [hostRef]);
  return state;
};

const waitFor = (find, ms = 4000) => new Promise((resolve) => {
  const t0 = Date.now();
  const tick = () => { const el = find(); if (el) return resolve(el); if (Date.now() - t0 > ms) return resolve(null); setTimeout(tick, 100); };
  tick();
});

// Pushes a query into the embed's own search box so its results dropdown opens with deep links.
export const searchEmbed = async (q) => {
  const query = (q || "").trim();
  if (!query) return false;
  if (/#help\/[a-z0-9-]+/.test(window.location.hash)) window.location.hash = "help";
  const input = await waitFor(() => document.querySelector("#fixlr-help .fxh-grid") && document.querySelector("#fixlr-help .fxh-search input"), 8000);
  if (!input) return false;
  input.value = query;
  input.dispatchEvent(new Event("input", { bubbles: true }));
  input.focus({ preventScroll: true });
  window.scrollTo({ top: input.getBoundingClientRect().top + window.scrollY - 120, behavior: "smooth" });
  return true;
};

const Fallback = ({ state }) => (
  <div className="rounded-2xl border border-mk-border bg-mk-surface/70 p-6 sm:p-8 text-center" data-testid={`help-embed-${state}`}>
    {state === "loading" ? (
      <><Loader2 className="mx-auto h-6 w-6 animate-spin text-mk-lime" /><p className="mt-3 text-sm text-mk-dim">Loading the help centre from fixlr.app…</p></>
    ) : (
      <>
        <div className="mx-auto grid h-11 w-11 place-items-center rounded-xl border border-amber-400/30 bg-amber-400/10 text-amber-300"><AlertTriangle className="h-5 w-5" /></div>
        <h2 className="mt-4 font-display text-xl font-bold text-mk-text">The help centre didn't load here</h2>
        <p className="mt-2 text-sm text-mk-dim max-w-md mx-auto">It's published from fixlr.app and isn't reachable from this page right now. Open it directly, or ask us — a human replies within the working day.</p>
        <div className="mt-5 flex flex-col sm:flex-row justify-center gap-2">
          <a href={HELP_APP_URL} target="_blank" rel="noreferrer" className="btn-lime justify-center" data-testid="help-open-app">Open help on fixlr.app <ExternalLink className="h-4 w-4" /></a>
          <Link to="/contact" className="btn-ghost justify-center" data-testid="help-contact-link">Contact support</Link>
        </div>
      </>
    )}
  </div>
);

const HelpEmbed = ({ onReady }) => {
  const hostRef = useRef(null);
  const state = useHelpEmbed(hostRef);
  useEffect(() => { if (state === "ready") onReady?.(); }, [state, onReady]);
  return (
    <section className="pb-20" data-testid="help-embed-section">
      <Container>
        <div ref={hostRef} data-testid="help-embed-host" className="min-h-[1px] text-left" data-state={state} />
        {state !== "ready" && <Fallback state={state} />}
        <noscript><p className="mt-4 text-sm text-mk-dim">Enable JavaScript to load the help centre, or open <a href={HELP_APP_URL}>fixlr.app/help</a>.</p></noscript>
      </Container>
    </section>
  );
};

const HeroSearch = ({ initial, onSearch }) => {
  const [q, setQ] = useState(initial || "");
  const [miss, setMiss] = useState(false);
  const run = async (value) => { setQ(value); setMiss(!(await onSearch(value))); };
  return (
    <div className="mt-8 max-w-2xl">
      <form onSubmit={(e) => { e.preventDefault(); run(q); }} className="relative" data-testid="help-hero-search-form">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-mk-muted" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the guides — RAMS, payroll, approve a quote…" className="input-dark !rounded-full !pl-12 !pr-32 !py-4 text-base" aria-label="Search the help centre" data-testid="help-hero-search" />
        <button type="submit" disabled={!q.trim()} className="btn-lime absolute right-1.5 top-1/2 -translate-y-1/2 !py-2.5 !px-5 disabled:opacity-40" data-testid="help-hero-search-btn">Search</button>
      </form>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-mk-muted" data-testid="help-popular">
        <span>Popular:</span>
        {POPULAR.map((p) => <button key={p} type="button" onClick={() => run(p)} className="rounded-full border border-mk-border px-2.5 py-1 text-mk-dim hover:border-mk-lime/60 hover:text-mk-text transition-colors" data-testid={`help-popular-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{p}</button>)}
      </div>
      {miss && <p className="mt-2 text-xs text-amber-300" data-testid="help-search-miss">The help centre is still loading — try again in a moment, or ask the assistant.</p>}
    </div>
  );
};

const Hero = ({ initialQuery, onSearch }) => (
  <section className="relative overflow-hidden pt-32 pb-10 sm:pt-40 grid-bg">
    <Container>
      <Kicker>Help centre</Kicker>
      <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-mk-text text-balance max-w-3xl" data-testid="help-title">How can we help?</h1>
      <p className="mt-6 text-lg text-mk-dim max-w-2xl leading-relaxed">Guides, how-tos and narrated walkthroughs for the office app, client portal, supplier portal and engineer app — the same help centre your team sees inside Fixlr.</p>
      <HeroSearch initial={initialQuery} onSearch={onSearch} />
      <div className="mt-8 flex flex-wrap gap-3">
        <CTAButton to="/contact" variant="ghost" testId="help-contact-btn"><LifeBuoy className="h-4 w-4" /> Contact support</CTAButton>
        <CTAButton href={SITE.loginUrl} variant="ghost" testId="help-signin-btn">Sign in to your workspace <ArrowRight className="h-4 w-4" /></CTAButton>
      </div>
      <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-3xl" data-testid="help-quick-links">
        {QUICK.map((q) => (
          <Link key={q.to} to={q.to} data-testid={`help-quick-${q.to.replace("/", "")}`} className="card-dark group flex items-center gap-3 p-4">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-mk-border bg-mk-bg text-mk-lime"><q.icon className="h-4 w-4" /></span>
            <span className="text-sm font-medium text-mk-text group-hover:text-mk-lime transition-colors">{q.label}</span>
          </Link>
        ))}
      </div>
    </Container>
  </section>
);

export default function Help() {
  usePageTitle(SEO["/help"].title, SEO["/help"].description, SEO["/help"].image);
  const { search } = useLocation();
  const navigate = useNavigate();
  const initialQuery = new URLSearchParams(search).get("q") || "";
  const pending = useRef(initialQuery);
  const onReady = useCallback(() => { if (pending.current) { const q = pending.current; pending.current = ""; searchEmbed(q); } }, []);
  // The embed deep-links guides as #help/<slug>; promote those to the shareable /help/<slug> page.
  useEffect(() => {
    const promote = () => { const m = /#help\/([a-z0-9-]+)/.exec(window.location.hash); if (m) navigate(`/help/${m[1]}`, { replace: true }); };
    promote();
    window.addEventListener("hashchange", promote);
    return () => window.removeEventListener("hashchange", promote);
  }, [navigate]);
  return (
    <div data-testid="help-page">
      <Hero initialQuery={initialQuery} onSearch={searchEmbed} />
      <HelpEmbed onReady={onReady} />
      <CTABand />
    </div>
  );
}
